import { Link } from "react-router-dom";
import LegalPage, { LegalSection } from "@/pages/legal/LegalPage";

const EFFECTIVE_DATE = "February 5, 2026";

export default function Refund() {
  return (
    <LegalPage
      testId="refund-page"
      eyebrow="Legal"
      title="Refund Policy"
      effectiveDate={EFFECTIVE_DATE}
    >
      <p>
        We want PostShifted to be worth every dollar. This policy explains when we will refund a
        payment. It applies to the paid <strong>Pro</strong> subscription
        ($19 USD per month). It is designed to be clear, fair, and easy to act on.
      </p>

      <LegalSection n={1} title="7-day satisfaction refund">
        <p>
          If you subscribed to Pro and are not happy with the Service, you may request a
          <strong> full refund within 7 days</strong> of your original purchase or renewal charge,
          provided you have not heavily used the Service during that period.
        </p>
        <p>
          &ldquo;Heavily used&rdquo; means having generated more than{" "}
          <strong>50 remixes</strong> in the billing cycle you are requesting a refund for. This
          threshold is well above what a typical evaluation would need and lets you kick the tyres
          properly before committing.
        </p>
      </LegalSection>

      <LegalSection n={2} title="How to request a refund">
        <p>
          Email{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>{" "}
          from the address on your account with the subject line{" "}
          <em>&ldquo;Refund request&rdquo;</em>. Please include:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>The email on your PostShifted account.</li>
          <li>The date of the charge you would like refunded.</li>
          <li>A short note on why the Service didn&apos;t work for you — this helps us improve.</li>
        </ul>
        <p>
          We acknowledge every request within one business day and process approved refunds through
          PayPal within 3-5 business days. The amount returns to your original payment method.
        </p>
      </LegalSection>

      <LegalSection n={3} title="After the 7-day window">
        <p>
          Subscription charges made after the first 7 days of a billing cycle are generally
          non-refundable, because you have already had substantial access to the Service. However,
          we will still consider refunds in exceptional cases such as:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>You were charged after already cancelling.</li>
          <li>A significant outage or defect prevented you from using the Service.</li>
          <li>Duplicate or accidental charges.</li>
        </ul>
        <p>
          Reach out and we&apos;ll look at each case on its own merits. Our goal is to treat you
          the way we would want to be treated as a customer.
        </p>
      </LegalSection>

      <LegalSection n={4} title="Cancellation vs refund">
        <p>
          <strong>Cancelling</strong> your subscription stops future renewals. You keep Pro access
          until the end of the current billing period, and no further charges will be made.
          Cancellation on its own is not the same as a refund — if you also want the current
          period refunded, request a refund following the steps above.
        </p>
        <p>
          You can cancel any time from{" "}
          <Link to="/settings" className="text-coral hover:text-coral-hover">Settings → Billing</Link>{" "}
          &rarr; <em>Cancel subscription</em>. You can also cancel directly from your PayPal
          account under <em>Settings → Payments → Manage automatic payments</em>.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Chargebacks">
        <p>
          We prefer to resolve billing questions directly. If you disagree with a charge, please
          email us first — we can usually help within a business day. Initiating a chargeback with
          your bank before contacting us may result in your account being suspended while the
          dispute is resolved.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Free trial or promotional credits">
        <p>
          Promotional credits, discounts, and free-trial extensions have no cash value and are not
          refundable.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Statutory rights">
        <p>
          This policy does not affect any statutory rights you have as a consumer under the laws
          of your country of residence (for example, the EU 14-day right of withdrawal for
          consumers). Those rights always apply on top of this policy.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Who processes the refund">
        <p>
          Payments and refunds are handled by our payment processor,{" "}
          <strong>PayPal</strong>. When we approve a refund, PayPal credits your original payment
          method. Timing can vary by bank (typically 3-10 business days).
        </p>
      </LegalSection>

      <LegalSection n={9} title="Contact">
        <p>
          Refund questions or requests:{" "}
          <a href="mailto:support@postshifted.com" className="text-coral hover:text-coral-hover">
            support@postshifted.com
          </a>
          . We read every email personally.
        </p>
      </LegalSection>

      <p className="pt-4 text-sm text-neutral-500">
        See also our{" "}
        <Link to="/terms" className="text-coral hover:text-coral-hover">Terms of Service</Link>{" "}
        and{" "}
        <Link to="/privacy" className="text-coral hover:text-coral-hover">Privacy Policy</Link>.
      </p>
    </LegalPage>
  );
}
